<template>
  <BaseCard
    mb
    :loading="loading"
    :heading="title"
    v-test="'invoices-card'"
  >
    <Flex :direction="'column'">
      <div :class="$style.header">
        <BaseText v-if="description">{{ description }}</BaseText>
        <BaseIcon
          v-if="hasFailedInvoice"
          name="alert"
          color="warning"
          size="l"
        />
      </div>
      <Flex :direction="'column'">
        <div
          v-for="invoice in invoices"
          :key="invoice.id"
          :class="$style.invoice"
        >
          <div>
            <BaseText>{{ filters.date(invoice.sealedAt) }}</BaseText>
            <BaseText v-test="`invoice-${invoice.id}-amount`">
              {{ filters.currency(invoice.total || 0, invoice.currency) }}
            </BaseText>
          </div>
          <BaseLabel :state="invoice.realStatus.toLowerCase()">
            {{ invoice.realStatus }}
          </BaseLabel>
          <BaseText>{{ invoice.invoiceNumber }}</BaseText>
          <div :class="$style.actionsContainer">
            <PayInvoiceButton
              v-if="invoice.realStatus !== InvoiceStatus.Paid"
              :stripeInvoice="invoice"
            />
            <BaseButton
              color="inverted"
              icon="download"
              @click="emit('downloadInvoice', invoice.id)"
              v-test="`invoices-card-invoice-${invoice.id}-download-action`"
            />
          </div>
        </div>
        <BaseText
          v-if="allInvoicesLink"
          @click="emit('allInvoicesLinkClick')"
          v-test="`invoices-card-all-invoices-link`"
        >
          {{ $t('admin.cards.invoices.see_all_inovices_link_label') }}
        </BaseText>
      </Flex>
    </Flex>
  </BaseCard>
</template>

<script
  lang="ts"
  setup
>
import { computed } from 'vue';
import filters from '@/filters';
import { InvoiceStatus, type StripeInvoice } from '@/types';
import PayInvoiceButton from './PayInvoiceButton.vue';

const props = defineProps<{
  invoices: StripeInvoice[];
  loading?: boolean;
  title?: string;
  description?: string;
  allInvoicesLink?: boolean;
}>();

const emit = defineEmits(['allInvoicesLinkClick', 'downloadInvoice']);

const hasFailedInvoice = computed(() =>
  props.invoices.some(
    (invoice: StripeInvoice) => invoice.realStatus === InvoiceStatus.Failed,
  ),
);
</script>

<style
  lang="scss"
  module
>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: $spacing;
  border-bottom: 1px solid var(--color-border);

  &:empty {
    display: none;
  }
}

.invoice {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $spacing;

  &:not(:first-child) {
    padding-top: $spacing;
    border-top: 1px solid var(--color-border);
  }
}

.actionsContainer {
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
  gap: $spacing;
}
</style>
